import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { streamingDataSlice } from '@src/providers/AppStateProvider/streamingDataSlice';
import type { AppState, AppStateListenerStart } from '@src/providers/AppStateProvider/types';
import {
  AllocationValueTypeEnum,
  BaseField,
  CounterpartyTypeEnum,
  EMPTY_ARRAY,
  Field,
  formattedDateForSubscription,
  getTypedKeys,
  isCompound,
  isSpot,
  NumericField,
  SecurityTypesUtils,
  SelectorField,
  SideEnum,
  SizeModeEnum,
  toBigWithDefault,
  Unit,
  type Allocation,
  type CareOrder,
  type FieldData,
  type QtyInputCurrencyOptions,
  type Security,
} from '@talos/kyoko';
import type { WritableDraft } from 'immer';
import { compact, get, uniq } from 'lodash-es';

import { parseDate } from '@talos/kyoko';
import { initialRefDataState } from '../OMSReferenceDataSlice';
import { OMSView } from '../OMSView';
import {
  disableAllocationFields,
  mapAllocationsToField,
  touchAllocations,
  updateSubAccountOrAllocationState,
} from '../allocationUtils';
import { numberIsPositive } from '../commonFieldRules';
import type { BasketSettleOffsetOverride, OMSReferenceDataState } from '../types';
import { quantityValidation } from './FieldRules';
import {
  UNITS_CURRENCY,
  type CareOrderCurrency,
  type CareOrderFormDependencies,
  type CareOrderFormState,
  type CareOrderState,
} from './types';
import { convertDateToDefaultCareOrderUTCDate, isFundSelectableInCareOrderForm } from './utils';

export const selectCareOrderBeingModified = (state: AppState) => state.careOrder.careOrderBeingModified;

const validate = (state: WritableDraft<CareOrderState>) => {
  validateQuantity(state);
};

const validateQuantity = (state: WritableDraft<CareOrderState>) => {
  state.form.quantityField = state.form.quantityField.validate([numberIsPositive, quantityValidation], state);
};

// Looks at the correct spots for you and returns the default sub account for the selected fund if found, otherwise undefined
function getDefaultSubAccountForSelectedFund(state: WritableDraft<CareOrderState>): string | undefined {
  const defaults = state.referenceData.settings.fundCareOrderDefaultSubAccounts;
  const fund = state.form.fundField.value;
  if (!defaults || !fund) {
    return undefined;
  }

  const subAccName = get(defaults, fund);
  if (
    !subAccName ||
    !state.dependencies.tradableSubAccounts.some(tradableSubAcc => tradableSubAcc.Name === subAccName)
  ) {
    // If the sub account in the defaulting config doesn't exist for the user, then make sure to not do anything here
    return undefined;
  }

  return subAccName;
}

function fieldsAreValid(form: CareOrderFormState, excludedFields: (keyof CareOrderFormState)[] = []) {
  const fields: BaseField<FieldData>[] = getTypedKeys(form)
    .filter(k => !excludedFields.includes(k as keyof CareOrderFormState))
    .filter(k => form[k] instanceof BaseField)
    .map(k => form[k]) as BaseField<FieldData>[];
  const fieldsHasError = fields.some(f => f.hasError);
  return !fieldsHasError;
}

export const getInitialState = (): CareOrderState => ({
  referenceData: initialRefDataState,
  dependencies: {
    tradableSubAccounts: EMPTY_ARRAY,
  },
  form: {
    fundField: new SelectorField({ name: 'Fund', isRequired: false, placeholder: 'Optional' }),
    symbolField: new SelectorField<Security>({ idProperty: 'Symbol', name: 'Symbol' }),
    sideField: new Field({ value: SideEnum.Buy }),
    quantityField: new NumericField({ name: 'Quantity', scale: undefined }), // quantity validation fully managed by FieldRules.quantityValidation
    careOrderCurrencyField: new SelectorField(),
    commentsField: new Field({ name: 'Comments', isRequired: false, value: '' }),
    externalCommentsField: new Field({ name: 'External Comments', isRequired: false, value: '' }),
    initiatingFromField: new Field({ name: 'InitiatingFrom', isRequired: false, value: '' }),
    groupField: new Field({ name: 'Group', isRequired: false, value: '' }),
    counterpartyField: new SelectorField({ name: 'Counterparty', isRequired: false, idProperty: 'Name' }),
    externalIDField: new Field({ name: 'External ID', isRequired: false }),
    settleDateField: new Field({ name: 'Settlement Date', isRequired: false, placeholder: 'Optional' }),
    allocationValueTypeField: new Field({ value: AllocationValueTypeEnum.Percentage }),
    allocations: EMPTY_ARRAY,
  },
  isLoading: false,
  initialized: false,
  careOrderBeingModified: undefined,
});

const disableInputFields = (state: WritableDraft<CareOrderState>, isDisabled: boolean) => {
  state.form.fundField = state.form.fundField.setDisabled(isDisabled);
  state.form.symbolField = state.form.symbolField.setDisabled(isDisabled);
  state.form.sideField = state.form.sideField.setDisabled(isDisabled);
  state.form.quantityField = state.form.quantityField.setDisabled(isDisabled);
  state.form.careOrderCurrencyField = state.form.careOrderCurrencyField.setDisabled(isDisabled);
  state.form.commentsField = state.form.commentsField.setDisabled(isDisabled);
  state.form.externalCommentsField = state.form.externalCommentsField.setDisabled(isDisabled);
  state.form.groupField = state.form.groupField.setDisabled(isDisabled);
  state.form.externalIDField = state.form.externalIDField.setDisabled(isDisabled);
  state.form.settleDateField = state.form.settleDateField.setDisabled(isDisabled);
  disableAllocationFields(state, isDisabled);
};

const careOrderSlice = createSlice({
  name: 'careOrder',
  initialState: getInitialState(),
  reducers: {
    setReferenceData: (state, action: PayloadAction<OMSReferenceDataState>) => {
      state.referenceData = action.payload;
      populateDropdownsFromRefData(state);
    },
    setDependencies: (state, action: PayloadAction<CareOrderFormDependencies>) => {
      state.dependencies = action.payload;
    },
    setSide: (state, action: PayloadAction<SideEnum | undefined>) => {
      state.form.sideField = state.form.sideField.updateValue(action.payload);
      applyBasketSettleOffsetOverride(state);
      validate(state);
    },
    setFund: (state, action: PayloadAction<string | undefined>) => {
      state.form.fundField = state.form.fundField.updateValue(action.payload);

      if (action.payload == null) {
        // fund cleared by the user
        const selectedSecurity = state.form.symbolField.value;

        // If the form is in Units, we need to make sure we can still submit in Units now that Fund is cleared.
        if (state.form.careOrderCurrencyField.value === UNITS_CURRENCY) {
          if (SecurityTypesUtils.Compound.isType(selectedSecurity)) {
            // A Compound security was selected "under" the Fund. Upon clearing the Fund, Units Qty is still okay.
            return;
          }

          // Else, some Spot symbol is selected. When removing the Fund in this case, Units is no longer allowed, so reset Currency + Quantity.
          state.form.careOrderCurrencyField = state.form.careOrderCurrencyField.updateValue(
            selectedSecurity?.BaseCurrency
          );
          resetQuantityField(state);
        }
        return;
      }

      const usingFundDefinitions = state.referenceData.settings.useFundDefinitionAndTradableIndexesStreams;

      // Two different flows for backwards compat reasons. The baskets flow is going to be deleted soon. Added in 2.55.0.
      if (usingFundDefinitions) {
        const fundSymbols = state.referenceData.funds.fundsBySymbol.get(action.payload)?.TradableSecurities;
        handleSymbolChange(state, fundSymbols?.at(0));
      } else {
        // using baskets
        const symbolForFund = state.referenceData.baskets.basketsList.find(
          bd => bd.BasketSymbol === action.payload
        )?.UnderlyingSecuritySymbol;
        handleSymbolChange(state, symbolForFund);
      }

      const defaultSubAccountForFund = getDefaultSubAccountForSelectedFund(state);
      if (defaultSubAccountForFund) {
        updateSubAccountOrAllocationState(state, { SubAccount: defaultSubAccountForFund });
      }
    },
    setSymbol: (state, action: PayloadAction<string | undefined>) => {
      handleSymbolChange(state, action.payload);
    },
    setQuantity: (state, action: PayloadAction<string>) => {
      state.form.quantityField = state.form.quantityField.updateValue(action.payload);
      validateQuantity(state);
    },
    setAllocations: (state, action: PayloadAction<Allocation[]>) => {
      state.form.allocations = mapAllocationsToField(action.payload, state.form.allocationValueTypeField.value, true);
    },
    setAllocationValueType: (state, action: PayloadAction<AllocationValueTypeEnum>) => {
      const mappedToNewUnit = state.form.allocations.map(pair => ({
        subAccountField: pair.subAccountField,
        valueField: pair.valueField
          .updateUnit(action.payload === AllocationValueTypeEnum.Percentage ? Unit.Percent : Unit.Decimal)
          .updateValue(
            state.form.allocationValueTypeField.value === AllocationValueTypeEnum.Percentage
              ? pair.valueField.displayValue
              : pair.valueField.value
          ),
      }));

      state.form.allocations = mappedToNewUnit;
      state.form.allocationValueTypeField = state.form.allocationValueTypeField.updateValue(action.payload);
    },
    setGroup: (state, action: PayloadAction<string>) => {
      state.form.groupField = state.form.groupField.updateValue(action.payload);
    },
    setComments: (state, action: PayloadAction<string>) => {
      state.form.commentsField = state.form.commentsField.updateValue(action.payload);
    },
    setExternalComments: (state, action: PayloadAction<string>) => {
      state.form.externalCommentsField = state.form.externalCommentsField.updateValue(action.payload);
    },
    setExternalID: (state, action: PayloadAction<string>) => {
      state.form.externalIDField = state.form.externalIDField.updateValue(action.payload);
    },
    setSettleDate: (state, action: PayloadAction<string | undefined>) => {
      state.form.settleDateField = state.form.settleDateField.updateValue(action.payload);
    },
    setInitiatingFrom: (state, action: PayloadAction<string>) => {
      state.form.initiatingFromField = state.form.initiatingFromField.updateValue(action.payload);
    },
    setCounterparty: (state, action: PayloadAction<string | undefined>) => {
      state.form.counterpartyField = state.form.counterpartyField.updateValueFromID(action.payload);
    },
    setCareOrderCurrency: (state, action: PayloadAction<CareOrderCurrency>) => {
      state.form.careOrderCurrencyField = state.form.careOrderCurrencyField.updateValue(action.payload);
      state.form.quantityField = state.form.quantityField.updateValue('', true);

      validateQuantity(state);
    },
    modify: (state, action: PayloadAction<CareOrder>) => {
      const {
        Symbol,
        Side,
        OrderQty,
        Currency,
        Comments,
        Group,
        Counterparty,
        ExternalComments,
        SettleDate,
        initiatingFrom,
        ExternalGroup,
        SubAccount,
        SizeMode,
        basketOrderDetails,
      } = action.payload;

      state.form.fundField = state.form.fundField
        .updateValue(action.payload.basketOrderDetails?.BasketSymbol)
        .setDisabled(true);
      const security = state.referenceData.securities.securitiesBySymbol.get(Symbol);
      state.form.symbolField = state.form.symbolField.updateValue(security).setDisabled(true);
      state.form.sideField = state.form.sideField.updateValue(Side).setDisabled(true);
      state.form.counterpartyField = state.form.counterpartyField.updateValueFromID(Counterparty);
      state.form.initiatingFromField = state.form.initiatingFromField.updateValue(initiatingFrom ?? '');
      state.form.commentsField = state.form.commentsField.updateValue(Comments ?? '');
      state.form.externalCommentsField = state.form.externalCommentsField.updateValue(ExternalComments ?? '');
      state.form.groupField = state.form.groupField.updateValue(Group ?? '');
      state.form.externalIDField = state.form.externalIDField.updateValue(ExternalGroup).setDisabled(true);
      state.form.settleDateField = state.form.settleDateField.updateValue(SettleDate).setDisabled(true);
      state.careOrderBeingModified = action.payload;
      updateSubAccountOrAllocationState(state, { SubAccount });
      disableAllocationFields(state, true); // backend doesn't support modifying SubAccount yet for some reason, so we disable the field.

      // If there exists a BasketOrderQty on the Care Order's Annotations (old code path), or the SizeMode is Units (new code path),
      // then this order was placed in units, and can only be modified in units.
      const isInUnits = basketOrderDetails?.BasketOrderQty != null || SizeMode === SizeModeEnum.Units;
      if (isInUnits) {
        state.form.careOrderCurrencyField = state.form.careOrderCurrencyField
          .updateValue(UNITS_CURRENCY)
          .setDisabled(true);

        // The two different codepaths here. Backend will remove the BasketOrder stuff in time as we build out a proper Fund model
        if (basketOrderDetails?.BasketOrderQty) {
          state.form.quantityField = state.form.quantityField.updateValue(basketOrderDetails.BasketOrderQty);
        } else {
          state.form.quantityField = state.form.quantityField.updateValue(OrderQty);
        }
      } else {
        // Else, the order was placed in standard Currency space (either Base or Quote)
        state.form.careOrderCurrencyField = state.form.careOrderCurrencyField.updateValue(Currency).setDisabled(true);
        state.form.quantityField = state.form.quantityField.updateValue(OrderQty);
      }

      // If you're modifying a completed care order, there are certain fields you cannot modify specifically
      if (action.payload.isComplete) {
        state.form.quantityField = state.form.quantityField.setDisabled(true);
      }

      // There is some special logic for the Settlement Date field specifically during modification, encapsulated in the care order class.
      state.form.settleDateField = state.form.settleDateField.setDisabled(!action.payload.isSettleDateModifiable);

      validateQuantity(state);
    },
    touchAll: state => {
      handleTouchAll(state, true);
    },
    setCareOrderForModify: (state, action: PayloadAction<CareOrder>) => {
      state.careOrderBeingModified = action.payload;
      validateQuantity(state);
    },
    /** Clean state keeps as many user selections around as possible, but gets them ready ("clean") for the next user workflow */
    cleanState: state => {
      const newState = getInitialState();

      // These values are the ones we remember between workflows (between placing multiple quick orders in succession)
      // These are easy wins to gain some speed and make sense to persist. All the rest are implicitly completely reset.
      newState.form.fundField = state.form.fundField.setTouched(false).setDisabled(false);
      newState.form.sideField = state.form.sideField.setTouched(false).setDisabled(false);
      newState.form.symbolField = state.form.symbolField.setTouched(false).setDisabled(false);
      newState.form.careOrderCurrencyField = state.form.careOrderCurrencyField.setTouched(false).setDisabled(false);
      newState.form.allocationValueTypeField = state.form.allocationValueTypeField.setTouched(false).setDisabled(false);
      newState.form.allocations = state.form.allocations.map(alloc => ({
        subAccountField: alloc.subAccountField.setTouched(false).setDisabled(false),
        valueField: alloc.valueField.setTouched(false).setDisabled(false),
      }));
      newState.form.settleDateField = state.form.settleDateField.setTouched(false).setDisabled(false);

      copyRefDataToNewState(newState, state);
      return newState;
    },
    /** clearState should remove all user selection in the form */
    clearState: state => {
      const newState = getInitialState();
      copyRefDataToNewState(newState, state);
      return newState;
    },
    setIsLoading: (state, { payload }: PayloadAction<boolean>) => {
      disableInputFields(state, payload);
      state.isLoading = payload;
    },
  },
});

/**
 * Directly modifies newState fields available items from the passed in (current) state
 * Used whenever we are resetting many fields at once. Just put this at the end before committing the state change
 * to make sure we don't lose the available items and ref data in the slice.
 */
function copyRefDataToNewState(newState: CareOrderState, state: WritableDraft<CareOrderState>) {
  newState.form.counterpartyField = newState.form.counterpartyField.updateAvailableItems(
    state.form.counterpartyField.availableItems
  );
  newState.form.fundField = newState.form.fundField.updateAvailableItems(state.form.fundField.availableItems);
  newState.referenceData = state.referenceData;
  newState.dependencies = state.dependencies;
}

function handleTouchAll(state: WritableDraft<CareOrderState>, touched: boolean) {
  getTypedKeys(state.form).forEach(key => {
    if (state.form[key] instanceof BaseField) {
      // @ts-expect-error - BaseField methods have trouble lining up on keyed calls
      state.form[key] = state.form[key].setTouched(touched);
    }
  });

  touchAllocations(state, touched);
}

const handleSymbolChange = (state: WritableDraft<CareOrderState>, symbol?: string) => {
  const security = symbol ? state.referenceData.securities.securitiesBySymbol.get(symbol) : undefined;
  if (!security) {
    return;
  }

  state.form.symbolField = state.form.symbolField.updateValue(security);

  if (isCompound(security)) {
    state.form.careOrderCurrencyField = state.form.careOrderCurrencyField.updateValue(UNITS_CURRENCY, true);
  } else {
    // Spot
    const { BaseCurrency, QuoteCurrency } = security;
    if (
      state.form.careOrderCurrencyField.value !== BaseCurrency &&
      state.form.careOrderCurrencyField.value !== QuoteCurrency
    ) {
      state.form.careOrderCurrencyField = state.form.careOrderCurrencyField.updateValue(BaseCurrency, true);
    }
  }

  resetQuantityField(state);
  resetSettleDate(state);
};

const resetQuantityField = (state: WritableDraft<CareOrderState>) => {
  const security = state.form.symbolField.value;
  if (!security) {
    return;
  }

  state.form.quantityField = state.form.quantityField.updateValue(security.NormalSize, true);
};

/**
 * Applies basket-specific settlement date offset overrides based on the selected fund and order side if supportAdjustedSettlementDate is enabled and there are settlement dates available.
 * If the user has manually set the settle date, we will not override it.
 * This is called after the order side changes (via `setSide` reducer) and after resetting the settlement date (via `resetSettleDate`).
 */
const applyBasketSettleOffsetOverride = (state: WritableDraft<CareOrderState>): void => {
  if (
    !state.referenceData.settings.supportAdjustedSettlementDate ||
    state.referenceData.adjustedSettlementDates.settlementDates.length === 0
  ) {
    // Not applicable. Either we don't support settlement date defaulting, or there are no overrides found
    return;
  }

  if (state.form.settleDateField.isTouched) {
    // we do support overrides, but the user has already set a settle date value manually, which we never want to override
    return;
  }

  const override: BasketSettleOffsetOverride = state.referenceData.settings.defaultBasketSettleOffsetOverride;
  const fund = state.form.fundField.value;
  const side = state.form.sideField.value;
  const fundOverride = override && fund ? override[fund] : undefined;
  const offset = fundOverride ? (side === SideEnum.Buy ? fundOverride.Buy : fundOverride.Sell) : undefined;
  const matchingDate =
    offset !== undefined && offset > 0
      ? state.referenceData.adjustedSettlementDates.settlementDates.find(date => date.numericOffset === offset)
      : undefined;
  state.form.settleDateField = state.form.settleDateField.updateValue(
    formattedDateForSubscription(
      convertDateToDefaultCareOrderUTCDate(
        parseDate((matchingDate || state.referenceData.adjustedSettlementDates.settlementDates[0]).AdjustedDate)
      )
    ),
    true
  );
};

/**
 * Resets the settlement date to the default (first available adjusted settlement date) and applies
 * any basket-specific offset overrides if supportAdjustedSettlementDate is enabled and there are settlement dates available.
 */
const resetSettleDate = (state: WritableDraft<CareOrderState>) => {
  if (
    !state.referenceData.settings.supportAdjustedSettlementDate ||
    state.referenceData.adjustedSettlementDates.settlementDates.length === 0
  ) {
    return;
  }

  state.form.settleDateField = state.form.settleDateField.updateValue(
    formattedDateForSubscription(
      convertDateToDefaultCareOrderUTCDate(
        parseDate(state.referenceData.adjustedSettlementDates.settlementDates[0]?.AdjustedDate)
      )
    ),
    true
  );

  applyBasketSettleOffsetOverride(state);
};

const populateDropdownsFromRefData = (state: WritableDraft<CareOrderState>) => {
  const { counterparties, funds, baskets } = state.referenceData;

  // [MKTP-1174] Only Allow Type AuthorizedParticipant Counterparties
  const filteredCounterparties = counterparties.filter(c => c.Type === CounterpartyTypeEnum.AuthorizedParticipant);

  state.form.counterpartyField = state.form.counterpartyField.updateAvailableItems(filteredCounterparties);

  const useFundDefinitions = state.referenceData.settings.useFundDefinitionAndTradableIndexesStreams;

  // Two different flows depending on a feature flag. We will be removing the baskets flow soon.
  if (useFundDefinitions) {
    state.form.fundField = state.form.fundField.updateAvailableItems(
      funds.fundsList.filter(isFundSelectableInCareOrderForm).map(f => f.Symbol)
    );
  } else {
    state.form.fundField = state.form.fundField.updateAvailableItems(baskets.basketsList.map(bd => bd.BasketSymbol));
  }
};

export const {
  setReferenceData,
  setDependencies,
  setQuantity,
  setSide,
  setCareOrderCurrency,
  setGroup,
  setSymbol,
  setAllocationValueType,
  setAllocations,
  setCounterparty,
  setComments,
  setExternalComments,
  setExternalID,
  setInitiatingFrom,
  setIsLoading,
  cleanState,
  clearState,
  modify,
  touchAll,
  setFund,
  setSettleDate,
} = careOrderSlice.actions;

// DERIVED state below
export function stringFieldCareOrderCurrency(
  field:
    | CareOrderState['form']['careOrderCurrencyField']
    | WritableDraft<CareOrderState['form']['careOrderCurrencyField']>
): string | undefined {
  return stringCareOrderCurrency(field.value);
}

export function stringCareOrderCurrency(currency: CareOrderCurrency | undefined): string | undefined {
  return typeof currency === 'string' ? currency : undefined;
}

export const selectCanRequestOrder = createSelector(
  (state: AppState) => state.careOrder.isLoading,
  (state: AppState) => state.careOrder.form,
  (state: AppState) => state.careOrder.careOrderBeingModified,
  (isLoading, form, careOrderBeingModified) => {
    if (careOrderBeingModified != null && careOrderBeingModified.isModifiable === false) {
      return false;
    }
    const hasValidFields = fieldsAreValid(form);
    return hasValidFields && !isLoading;
  }
);

export const selectQuantityCurrencyOptions = createSelector(
  (state: AppState) => state.careOrder.form.symbolField.value,
  (state: AppState) => state.careOrder.form.fundField.value,
  (security, fund): QtyInputCurrencyOptions<CareOrderCurrency>[] => {
    if (!security) {
      return [];
    }

    const unitsCcyOption: QtyInputCurrencyOptions<CareOrderCurrency> = { currency: UNITS_CURRENCY, label: 'Units' };

    // The selected security is Type = Compound
    if (SecurityTypesUtils.Compound.isType(security)) {
      return [unitsCcyOption];
    }

    // Else, the selected security is type = Spot. We can still do units here (but only if a Fund is selected)
    const spotOptions: QtyInputCurrencyOptions<CareOrderCurrency>[] = uniq(
      compact([security.BaseCurrency, security.QuoteCurrency])
    ).map(currency => ({
      currency,
      label: currency,
    }));

    if (fund != null) {
      spotOptions.push(unitsCcyOption);
    }

    return spotOptions;
  }
);

// Create a stable view where we remove the huge amount of derivatives. The unstable filtering is done in a later step. We dont want to always do this op
const selectRelevantSecurities = createSelector(
  (state: AppState) => state.referenceData.securities.securitiesList,
  securitiesList => {
    return securitiesList.filter(sec => isSpot(sec) || isCompound(sec));
  }
);

export const selectAvailableSecurities = createSelector(
  selectRelevantSecurities,
  (state: AppState) => state.careOrder.form.fundField.value,
  (state: AppState) => state.referenceData.funds.fundsBySymbol,
  (state: AppState) => state.referenceData.baskets.basketsList,
  (state: AppState) => state.referenceData.settings.useFundDefinitionAndTradableIndexesStreams,
  (securitiesList, selectedFund, fundsBySymbol, basketsList, useFundDefinitions) => {
    let tradableSymbolsForFundSet: Set<string> | undefined = undefined;

    // If there is a fund selected, we have to filter the available securities to only those supported by that fund
    // Depending on if we are using the new FundDefinitions, or the old Basket records, we figure this out in different ways below
    if (selectedFund) {
      if (useFundDefinitions) {
        const fundDef = selectedFund ? fundsBySymbol.get(selectedFund) : undefined;
        tradableSymbolsForFundSet = fundDef ? new Set(fundDef.TradableSecurities) : undefined;
      } else {
        // we are relying on baskets. This is the old flow which will be removed soon
        const basket = basketsList.find(basket => basket.BasketSymbol === selectedFund);
        tradableSymbolsForFundSet = basket ? new Set([basket.UnderlyingSecuritySymbol]) : undefined;
      }
    }

    return securitiesList.filter(security => {
      if (tradableSymbolsForFundSet && !tradableSymbolsForFundSet.has(security.Symbol)) {
        return false;
      }

      return true;
    });
  }
);

export const selectAvailableCounterparties = createSelector(
  (state: AppState) => state.referenceData.counterparties,
  counterparties => {
    return counterparties.filter(c => c.Type === CounterpartyTypeEnum.AuthorizedParticipant);
  }
);

export const selectCurrentFund = createSelector(
  (state: AppState) => state.careOrder.form.fundField.value,
  (state: AppState) => state.referenceData.funds.fundsBySymbol,
  (fund, fundsBySymbol) => {
    return fund ? fundsBySymbol.get(fund) : undefined;
  }
);

export const selectCurrentBasket = createSelector(
  (state: AppState) => state.careOrder.form.fundField.value,
  (state: AppState) => state.referenceData.baskets.basketsList,
  (fund, basketsList) => {
    if (!fund) {
      return undefined;
    }

    return basketsList.find(b => b.BasketSymbol === fund);
  }
);

/** Wraps the symbolField.isDisabled in extra derived logic for better UX */
export const selectDisableSecuritySelector = createSelector(
  (state: AppState) => state.careOrder.form.symbolField,
  selectAvailableSecurities,
  selectCurrentBasket,
  (symbolField, availableSecurities, selectedBasket) => {
    if (
      selectedBasket != null &&
      availableSecurities.length === 1 &&
      symbolField.value?.Symbol === selectedBasket.UnderlyingSecuritySymbol
    ) {
      // If there's a basket selected and the selected symbol is that basket's underlying symbol, the user can't do anything else, so we disable the selector
      return true;
    }

    return symbolField.isDisabled;
  }
);

/**
 * In the case where we are modifying a CareOrder placed in units, we want to modify it using the Basket as is defined on the care order.
 * This selector takes this into consideration, giving you the effective BasketRatios
 */
export const selectEffectiveBasketRatio = createSelector(
  (state: AppState) => state.careOrder.careOrderBeingModified,
  selectCurrentBasket,
  (co, currentBasket) => {
    if (co && co.basketOrderDetails?.BasketRatio && co.basketOrderDetails.BasketRatioDate) {
      return {
        BasketRatio: co.basketOrderDetails.BasketRatio,
        BasketRatioDate: co.basketOrderDetails.BasketRatioDate,
      };
    }

    if (currentBasket) {
      return {
        BasketRatio: currentBasket.QuantityDetails.BasketRatio,
        BasketRatioDate: currentBasket.QuantityDetails.BasketRatioDate,
      };
    }

    return undefined;
  }
);

/** Converts the unit qty to token (base ccy) qty per leg. Even for single-token, we will return an array of one item */
export const selectUnitsConvertedToBase = createSelector(
  (state: AppState) => state.careOrder.form.symbolField.value,
  (state: AppState) => state.careOrder.form.careOrderCurrencyField.value,
  (state: AppState) => state.careOrder.form.quantityField.value,
  selectEffectiveBasketRatio,
  (security, currency, quantity, basketRatio) => {
    if (!security || currency !== UNITS_CURRENCY) {
      // This conversion is not applicable if the user hasn't selected units or if the security is not of type compound
      return undefined;
    }

    const quantityBig = toBigWithDefault(quantity, 0);

    if (SecurityTypesUtils.Compound.isType(security)) {
      return security.Legs.map(leg => ({
        symbol: leg.Symbol,
        quantity: toBigWithDefault(leg.SizeRatio, 0)
          .times(toBigWithDefault(security.NotionalMultiplier, 1))
          .times(quantityBig)
          .toFixed(),
      }));
    }

    // Else we are Spot. In this case we can only select Units if there is a fund (basket) selected.
    // This is an old flow which will be removed (replaced by compound securities for both single and multi-token)
    if (SecurityTypesUtils.Spot.isType(security) && basketRatio) {
      return [
        {
          symbol: security.Symbol,
          quantity: toBigWithDefault(basketRatio.BasketRatio, 0).times(quantityBig).toFixed(),
        },
      ];
    }

    // Else we hit something unknown... not a big deal, this is just for UX
    return undefined;
  }
);

export function setupListeners(startListening: AppStateListenerStart) {
  /**
   * Subscribe (via RTK query) to the care order with the given OrderID,
   * so we can update the form if the care order changes.
   */
  const unsubscribeCareOrderForModify = startListening({
    actionCreator: modify,
    effect: async (action, listener) => {
      listener.cancelActiveListeners();
      const orderID = action.payload.OrderID;
      const queryArg = { OrderID: orderID };

      const selectCareOrder = streamingDataSlice.endpoints.getCareOrders.select(queryArg);
      // Check if there's already a cached value for this OrderID
      const careOrder = selectCareOrder(listener.getState()).data?.get(orderID);
      if (careOrder != null) {
        listener.dispatch(careOrderSlice.actions.setCareOrderForModify(careOrder));
      }

      // Listen for changes to the care order
      const unsubscribeCareOrderForModifyListener = startListening({
        predicate: (_, next, prev) => selectCareOrder(next) !== selectCareOrder(prev),
        effect: (_, listener) => {
          const careOrder = selectCareOrder(listener.getState()).data?.get(orderID);
          if (careOrder == null) {
            return;
          }
          listener.dispatch(careOrderSlice.actions.setCareOrderForModify(careOrder));
        },
      });
      const careOrders = listener.dispatch(streamingDataSlice.endpoints.getCareOrders.initiate(queryArg));

      // If this effect gets cancelled (e.g. the user closes the form), we should unsubscribe from the care order query
      listener.signal.addEventListener('abort', () => {
        careOrders.unsubscribe();
        unsubscribeCareOrderForModifyListener();
      });

      await listener.condition(() => listener.getState().OMS.openedView !== OMSView.CareOrderForm);
    },
  });

  return () => {
    unsubscribeCareOrderForModify();
  };
}

export const careOrderSliceReducer = careOrderSlice.reducer;
